import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import CookieConsent from './components/CookieConsent';
import AnalyticsLoader from './components/AnalyticsLoader';
import MetaPixelTracker from './components/MetaPixelTracker';
import PurchaseAnalyticsBridge from './components/PurchaseAnalyticsBridge';
import LoadingScreen from './components/LoadingScreen';
import HomePage from './components/HomePage';
import { AppShellBridgeProvider } from './contexts/AppShellContext';
import { PublicShellAuthProvider } from './contexts/PublicShellAuthProvider';
import { readCookieConsentState } from './services/cookiePreferences';
import { isKnownAppPath, isKnownBlogPath, normalizeAppPath } from './utils/appRoutes';
import { captureReferralAttributionFromCurrentUrl } from './utils/referralAttribution.mjs';
import { hasAuthActionQuery } from './utils/authActionQuery.mjs';
import AppConfirmationDialog from './components/AppConfirmationDialog';
import AppErrorBoundary from './components/AppErrorBoundary';

const StudioAppShell = lazy(() => import('./StudioAppShell'));
const AuthAction = lazy(() => import('./components/AuthAction'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const TrustPage = lazy(() => import('./components/TrustPage'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const AlternativesPage = lazy(() => import('./components/AlternativesPage'));
const PaddlePaymentLinkPage = lazy(() => import('./components/PaddlePaymentLinkPage'));

const VsRoute = lazy(async () => {
  const [{ getVsPageByPath }, { default: VsComparisonPage }] = await Promise.all([
    import('./data/vsPages'),
    import('./components/VsComparisonPage')
  ]);

  return {
    default: function VsRouteContent({ pathname }) {
      const page = getVsPageByPath(pathname);
      return page ? <VsComparisonPage page={page} /> : <NotFoundPage />;
    }
  };
});

const HelpArticleRoute = lazy(async () => {
  const [{ getHelpArticleByPath }, { default: HelpArticlePage }] = await Promise.all([
    import('./data/helpArticles.js'),
    import('./components/HelpArticlePage')
  ]);

  return {
    default: function HelpArticleRouteContent({ pathname }) {
      const article = getHelpArticleByPath(pathname);
      return article ? <HelpArticlePage page={article} /> : <NotFoundPage />;
    }
  };
});

let resolvedToolOrNotFoundRoute = null;
let toolOrNotFoundRoutePromise = null;

const loadToolOrNotFoundRoute = () => {
  if (!toolOrNotFoundRoutePromise) {
    toolOrNotFoundRoutePromise = Promise.all([
      import('./data/toolLandingPages'),
      import('./components/ToolLandingPage')
    ]).then(([{ getToolLandingPageByPath }, { default: ToolLandingPage }]) => {
      resolvedToolOrNotFoundRoute = function ToolOrNotFoundRouteContent({ pathname }) {
        const page = getToolLandingPageByPath(pathname);
        return page ? <ToolLandingPage page={page} /> : <NotFoundPage />;
      };

      return { default: resolvedToolOrNotFoundRoute };
    }).catch((error) => {
      toolOrNotFoundRoutePromise = null;
      throw error;
    });
  }

  return toolOrNotFoundRoutePromise;
};

const ToolOrNotFoundRoute = lazy(loadToolOrNotFoundRoute);

// Public tool pages are prerendered. The client bootstrap awaits this split
// bundle before replacing that HTML, so visitors never see the global app
// loader while the landing page becomes interactive.
export const preloadToolOrNotFoundRoute = () => loadToolOrNotFoundRoute();

const RouteFallback = () => <LoadingScreen />;

const hasPaddleTransactionQuery = (search = '') => {
  try {
    return Boolean(String(new URLSearchParams(search).get('_ptxn') || '').trim());
  } catch {
    return false;
  }
};

export const shouldPreloadToolOrNotFoundRoute = (pathname = '', search = '') => {
  if (hasAuthActionQuery(search) || hasPaddleTransactionQuery(search)) return false;

  const normalizedPath = normalizeAppPath(pathname);
  if (
    !normalizedPath
    || normalizedPath === '/'
    || normalizedPath === '/pay'
    || normalizedPath === '/alternatives'
    || normalizedPath === '/stocktag-vs-alternatives'
    || normalizedPath === '/stocktag-vs-alternatifleri'
    || normalizedPath.startsWith('/vs/')
    || normalizedPath === '/privacy'
    || normalizedPath === '/terms'
    || ['/about', '/editorial-policy', '/contact'].includes(normalizedPath)
    || isKnownAppPath(normalizedPath)
    || isKnownBlogPath(normalizedPath)
    || normalizedPath.startsWith('/help/')
    || normalizedPath.startsWith('/video/')
  ) {
    return false;
  }

  return true;
};

const withRouteSuspense = (node) => (
  <Suspense fallback={<RouteFallback />}>
    {node}
  </Suspense>
);

const PublicShellProviders = ({ children, deferHomeBootstrap = true }) => (
  <PublicShellAuthProvider deferHomeBootstrap={deferHomeBootstrap}>
    {children}
  </PublicShellAuthProvider>
);

const AppProviders = ({ children }) => {
  const [cookiePreferences, setCookiePreferences] = useState(() => readCookieConsentState().preferences);

  return (
    <HelmetProvider>
      <AnalyticsLoader preferences={cookiePreferences} />
      <MetaPixelTracker consent={Boolean(cookiePreferences?.advertising)} />
      <PurchaseAnalyticsBridge />
      {children}
      <AppConfirmationDialog />
      <CookieConsent onConsentChange={setCookiePreferences} />
    </HelmetProvider>
  );
};

const AppRouter = () => {
  useEffect(() => {
    captureReferralAttributionFromCurrentUrl();
  }, []);

  const authAction = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const oobCode = urlParams.get('oobCode');

    if (mode && oobCode && ['resetPassword', 'verifyEmail', 'recoverEmail'].includes(mode)) {
      return { mode, oobCode };
    }

    return null;
  }, []);

  if (authAction) {
    const handleAuthComplete = () => {
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.reload();
    };

    return (
      withRouteSuspense(
        <PublicShellProviders>
          <AppShellBridgeProvider>
            <AuthAction
              mode={authAction.mode}
              oobCode={authAction.oobCode}
              onComplete={handleAuthComplete}
            />
          </AppShellBridgeProvider>
        </PublicShellProviders>
      )
    );
  }

  const pathname = window.location.pathname;
  const search = window.location.search;
  const normalizedPath = normalizeAppPath(pathname);
  const hasPaddleTransaction = hasPaddleTransactionQuery(search);

  if (hasPaddleTransaction || normalizedPath === '/pay') {
    return withRouteSuspense(<PaddlePaymentLinkPage />);
  }

  if (
    normalizedPath === '/alternatives'
    || normalizedPath === '/stocktag-vs-alternatives'
    || normalizedPath === '/stocktag-vs-alternatifleri'
  ) {
    return withRouteSuspense(<AlternativesPage />);
  }

  if (normalizedPath.startsWith('/vs/')) {
    return withRouteSuspense(<VsRoute pathname={pathname} />);
  }

  if (normalizedPath === '/privacy') {
    return withRouteSuspense(<PrivacyPolicy />);
  }

  if (normalizedPath === '/terms') {
    return withRouteSuspense(<TermsOfService />);
  }

  if (['/about', '/editorial-policy', '/contact'].includes(normalizedPath)) {
    return withRouteSuspense(<TrustPage path={normalizedPath} />);
  }

  if (normalizedPath === '/') {
    return (
      <PublicShellProviders>
        <HomePage />
      </PublicShellProviders>
    );
  }

  if (isKnownAppPath(normalizedPath) || isKnownBlogPath(normalizedPath)) {
    return withRouteSuspense(<StudioAppShell />);
  }

  if (normalizedPath.startsWith('/help/') || normalizedPath.startsWith('/video/')) {
    return withRouteSuspense(<HelpArticleRoute pathname={pathname} />);
  }

  if (resolvedToolOrNotFoundRoute) {
    const ResolvedToolOrNotFoundRoute = resolvedToolOrNotFoundRoute;
    return withRouteSuspense(<ResolvedToolOrNotFoundRoute pathname={pathname} />);
  }

  return withRouteSuspense(<ToolOrNotFoundRoute pathname={pathname} />);
};

const App = () => {
  return (
    <AppProviders>
      <AppErrorBoundary>
        <AppRouter />
      </AppErrorBoundary>
    </AppProviders>
  );
};

export default App;
